import { useState, useEffect } from "react";

const API_BASE = "http://localhost:8000";

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  not_started: { label: "Not Started", color: "#475569", bg: "#47556918" },
  in_progress: { label: "In Progress", color: "#f59e0b", bg: "#f59e0b18" },
  blocked:     { label: "Blocked",     color: "#ef4444", bg: "#ef444418" },
  complete:    { label: "Complete",    color: "#22c55e", bg: "#22c55e18" },
};

const PRIORITY_CONFIG = {
  critical: { label: "Critical", color: "#ef4444", order: 0 },
  high:     { label: "High",     color: "#f97316", order: 1 },
  medium:   { label: "Medium",   color: "#eab308", order: 2 },
  low:      { label: "Low",      color: "#22c55e", order: 3 },
};

const CATEGORY_LABELS = {
  identity_access:        "Identity & Access Management",
  network_security:       "Network Security",
  endpoint_security:      "Endpoint Security",
  endpoint_protection:    "Endpoint Protection",
  data_protection:        "Data Protection",
  incident_response:      "Incident Response",
  vulnerability_management: "Vulnerability Management",
  cloud_security:         "Cloud Security",
  application_security:   "Application Security",
  third_party_risk:       "Third-Party Risk Management",
  compliance_governance:  "Compliance & Governance",
  security_awareness:     "Security Awareness & Training",
  physical_security:      "Physical Security",
  business_continuity:    "Business Continuity",
  backup_recovery:        "Backup & Recovery",
  logging_monitoring:     "Logging & Monitoring",
  asset_management:       "Asset Management",
};

const GAP_TO_PRIORITY = (gap) => {
  if (gap >= 3) return "critical";
  if (gap >= 2) return "high";
  if (gap >= 1) return "medium";
  return "low";
};

const PHASE_FROM_PRIORITY = (priority) => {
  if (priority === "critical") return 1;
  if (priority === "high")     return 1;
  if (priority === "medium")   return 2;
  return 3;
};

// ─── Task generation from assessment gaps ────────────────────────────────────

const TASK_TEMPLATES = {
  identity_access: [
    "Implement multi-factor authentication (MFA) for all privileged accounts",
    "Conduct user access review and remove orphaned accounts",
    "Deploy Privileged Access Management (PAM) solution",
    "Document and enforce least-privilege access policies",
  ],
  network_security: [
    "Review and harden firewall ruleset — remove unused rules",
    "Implement network segmentation for critical systems",
    "Deploy IDS/IPS and configure alerting thresholds",
    "Document and test network topology change management process",
  ],
  data_protection: [
    "Inventory and classify sensitive data across all systems",
    "Implement encryption at rest for customer financial data",
    "Deploy DLP (Data Loss Prevention) tooling",
    "Establish and test data retention and disposal procedures",
  ],
  incident_response: [
    "Develop and document formal Incident Response Plan",
    "Conduct tabletop exercise with key stakeholders",
    "Establish 24/7 security incident escalation path",
    "Integrate IR procedures with regulatory notification requirements",
  ],
  vulnerability_management: [
    "Establish recurring vulnerability scanning schedule (weekly internal, quarterly external)",
    "Define and enforce patching SLAs by severity (critical: 7 days, high: 30 days)",
    "Integrate vulnerability findings into risk register",
    "Conduct annual penetration test by qualified third party",
  ],
  cloud_security: [
    "Enable Cloud Security Posture Management (CSPM) tooling",
    "Review and enforce IAM policies across cloud accounts",
    "Enable CloudTrail / audit logging for all cloud activity",
    "Implement cloud workload protection and runtime monitoring",
  ],
  endpoint_security: [
    "Deploy EDR (Endpoint Detection & Response) on all endpoints",
    "Enforce disk encryption on all laptops and mobile devices",
    "Implement USB/removable media controls",
    "Establish endpoint compliance baseline and monitoring",
  ],
  endpoint_protection: [
    "Deploy next-generation antivirus on all endpoints",
    "Enable application whitelisting for critical systems",
    "Configure automated threat detection and quarantine",
    "Establish patch compliance monitoring dashboard",
  ],
  application_security: [
    "Implement SAST/DAST in CI/CD pipeline",
    "Conduct OWASP Top 10 review of customer-facing applications",
    "Establish secure code review process and developer training",
    "Deploy WAF (Web Application Firewall) for public-facing apps",
  ],
  third_party_risk: [
    "Complete vendor inventory and tiered risk classification",
    "Implement third-party security questionnaire process (annual)",
    "Review and update vendor contracts for cybersecurity requirements",
    "Establish ongoing vendor monitoring program",
  ],
  compliance_governance: [
    "Develop and publish Information Security Policy",
    "Assign Qualified Individual for cybersecurity oversight",
    "Establish annual Board-level cybersecurity reporting cadence",
    "Complete regulatory gap assessment (GLBA/SOX/PCI-DSS)",
  ],
  security_awareness: [
    "Launch mandatory security awareness training program",
    "Conduct phishing simulation campaign and measure click rates",
    "Establish role-based security training for IT/finance/HR staff",
    "Create security awareness metrics dashboard for leadership",
  ],
  physical_security: [
    "Audit physical access controls to server rooms and data centers",
    "Implement visitor logging and escort procedures",
    "Review CCTV coverage and retention policies",
    "Conduct physical security risk assessment",
  ],
  business_continuity: [
    "Develop and document Business Continuity Plan (BCP)",
    "Conduct annual BCP tabletop exercise",
    "Define and test Recovery Time Objectives (RTO) for critical systems",
    "Establish crisis communications procedures",
  ],
  backup_recovery: [
    "Verify backup completeness and test restoration procedures quarterly",
    "Implement offsite / immutable backup for critical financial data",
    "Document and test disaster recovery runbook",
    "Establish backup monitoring and alerting",
  ],
  logging_monitoring: [
    "Deploy SIEM and configure baseline alerting rules",
    "Enable audit logging on all financial systems (SOX requirement)",
    "Establish daily log review process",
    "Configure alerting for suspicious authentication activity",
  ],
  asset_management: [
    "Complete hardware and software asset inventory",
    "Implement automated asset discovery and tracking",
    "Establish asset lifecycle management procedures",
    "Define and enforce decommissioning standards",
  ],
};

function generateTasksFromGaps(responses) {
  const tasks = [];
  let idCounter = 1;

  const sorted = Object.entries(responses)
    .map(([key, val]) => ({
      key,
      gap: (val.target || 5) - (val.score || 0),
      current: val.score || 0,
      target: val.target || 5,
    }))
    .filter((g) => g.gap > 0)
    .sort((a, b) => b.gap - a.gap);

  for (const { key, gap, current, target } of sorted) {
    const templates = TASK_TEMPLATES[key] || [
      `Improve ${CATEGORY_LABELS[key] || key} from Level ${current} to Level ${target}`,
    ];
    const priority = GAP_TO_PRIORITY(gap);
    const phase = PHASE_FROM_PRIORITY(priority);

    for (const title of templates) {
      tasks.push({
        id: `task_${idCounter++}`,
        title,
        category: key,
        categoryLabel: CATEGORY_LABELS[key] || key,
        priority,
        phase,
        status: "not_started",
        assignee: "",
        notes: "",
        dependencies: [],
        createdAt: new Date().toISOString(),
        autoGenerated: true,
      });
    }
  }
  return tasks;
}

// ─── Subcomponents ────────────────────────────────────────────────────────────

function StatusBadge({ status, onChange }) {
  const [open, setOpen] = useState(false);
  const cfg = STATUS_CONFIG[status];
  return (
    <div style={{ position: "relative" }}>
      <button
        style={{ ...styles.badge, background: cfg.bg, color: cfg.color, cursor: "pointer", border: `1px solid ${cfg.color}30` }}
        onClick={() => setOpen((v) => !v)}
      >
        {cfg.label} ▾
      </button>
      {open && (
        <div style={styles.dropdown}>
          {Object.entries(STATUS_CONFIG).map(([key, c]) => (
            <button
              key={key}
              style={{ ...styles.dropdownItem, color: c.color }}
              onClick={() => { onChange(key); setOpen(false); }}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PriorityDot({ priority }) {
  const cfg = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.medium;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: cfg.color, display: "inline-block", flexShrink: 0 }} />
      <span style={{ fontSize: 11, color: cfg.color, fontWeight: 700, letterSpacing: "0.04em" }}>{cfg.label}</span>
    </span>
  );
}

function TaskRow({ task, onUpdate, onDelete, allTasks }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{ ...styles.taskCard, opacity: task.status === "complete" ? 0.6 : 1 }}>
      <div style={styles.taskMain}>
        {/* Checkbox */}
        <button
          style={{ ...styles.checkbox, ...(task.status === "complete" ? styles.checkboxDone : {}) }}
          onClick={() => onUpdate(task.id, { status: task.status === "complete" ? "not_started" : "complete" })}
        >
          {task.status === "complete" && "✓"}
        </button>

        {/* Content */}
        <div style={styles.taskContent}>
          <div style={styles.taskTitleRow}>
            <span style={{ ...styles.taskTitle, textDecoration: task.status === "complete" ? "line-through" : "none" }}>
              {task.title}
            </span>
            {task.autoGenerated && (
              <span style={styles.autoTag}>auto</span>
            )}
          </div>
          <div style={styles.taskMeta}>
            <span style={styles.categoryTag}>{task.categoryLabel}</span>
            <PriorityDot priority={task.priority} />
            <span style={styles.phaseTag}>Phase {task.phase}</span>
            {task.assignee && <span style={styles.assigneeTag}>👤 {task.assignee}</span>}
          </div>
        </div>

        {/* Actions */}
        <div style={styles.taskActions}>
          <StatusBadge status={task.status} onChange={(s) => onUpdate(task.id, { status: s })} />
          <button style={styles.expandBtn} onClick={() => setExpanded((v) => !v)}>
            {expanded ? "▲" : "▼"}
          </button>
          <button style={styles.deleteBtn} onClick={() => onDelete(task.id)}>✕</button>
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div style={styles.taskDetail}>
          <div style={styles.detailGrid}>
            <div style={styles.detailField}>
              <label style={styles.detailLabel}>Assignee</label>
              <input
                style={styles.detailInput}
                value={task.assignee}
                onChange={(e) => onUpdate(task.id, { assignee: e.target.value })}
                placeholder="Engineer name or team"
              />
            </div>
            <div style={styles.detailField}>
              <label style={styles.detailLabel}>Priority</label>
              <select
                style={styles.detailSelect}
                value={task.priority}
                onChange={(e) => onUpdate(task.id, { priority: e.target.value })}
              >
                {Object.entries(PRIORITY_CONFIG).map(([key, cfg]) => (
                  <option key={key} value={key}>{cfg.label}</option>
                ))}
              </select>
            </div>
            <div style={styles.detailField}>
              <label style={styles.detailLabel}>Phase</label>
              <select
                style={styles.detailSelect}
                value={task.phase}
                onChange={(e) => onUpdate(task.id, { phase: parseInt(e.target.value) })}
              >
                <option value={1}>Phase 1 (0–90 days)</option>
                <option value={2}>Phase 2 (90–180 days)</option>
                <option value={3}>Phase 3 (180+ days)</option>
              </select>
            </div>
          </div>
          <div style={styles.detailField}>
            <label style={styles.detailLabel}>Notes</label>
            <textarea
              style={styles.detailTextarea}
              value={task.notes}
              onChange={(e) => onUpdate(task.id, { notes: e.target.value })}
              placeholder="Implementation notes, blockers, links..."
              rows={2}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function AddTaskModal({ onAdd, onClose }) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("identity_access");
  const [priority, setPriority] = useState("medium");
  const [phase, setPhase] = useState(1);
  const [assignee, setAssignee] = useState("");
  const [notes, setNotes] = useState("");

  const handleSubmit = () => {
    if (!title.trim()) return;
    onAdd({
      id: `task_manual_${Date.now()}`,
      title: title.trim(),
      category,
      categoryLabel: CATEGORY_LABELS[category] || category,
      priority,
      phase,
      status: "not_started",
      assignee,
      notes,
      dependencies: [],
      createdAt: new Date().toISOString(),
      autoGenerated: false,
    });
    onClose();
  };

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>Add Task</h3>
          <button style={styles.modalClose} onClick={onClose}>✕</button>
        </div>
        <div style={styles.modalBody}>
          <div style={styles.detailField}>
            <label style={styles.detailLabel}>Task Title *</label>
            <input
              style={styles.detailInput}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Describe the remediation task..."
              autoFocus
            />
          </div>
          <div style={styles.detailGrid}>
            <div style={styles.detailField}>
              <label style={styles.detailLabel}>Category</label>
              <select style={styles.detailSelect} value={category} onChange={(e) => setCategory(e.target.value)}>
                {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div style={styles.detailField}>
              <label style={styles.detailLabel}>Priority</label>
              <select style={styles.detailSelect} value={priority} onChange={(e) => setPriority(e.target.value)}>
                {Object.entries(PRIORITY_CONFIG).map(([key, cfg]) => (
                  <option key={key} value={key}>{cfg.label}</option>
                ))}
              </select>
            </div>
            <div style={styles.detailField}>
              <label style={styles.detailLabel}>Phase</label>
              <select style={styles.detailSelect} value={phase} onChange={(e) => setPhase(parseInt(e.target.value))}>
                <option value={1}>Phase 1 (0–90 days)</option>
                <option value={2}>Phase 2 (90–180 days)</option>
                <option value={3}>Phase 3 (180+ days)</option>
              </select>
            </div>
            <div style={styles.detailField}>
              <label style={styles.detailLabel}>Assignee</label>
              <input
                style={styles.detailInput}
                value={assignee}
                onChange={(e) => setAssignee(e.target.value)}
                placeholder="Engineer or team"
              />
            </div>
          </div>
          <div style={styles.detailField}>
            <label style={styles.detailLabel}>Notes</label>
            <textarea
              style={styles.detailTextarea}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional context..."
              rows={2}
            />
          </div>
        </div>
        <div style={styles.modalFooter}>
          <button style={styles.cancelBtn} onClick={onClose}>Cancel</button>
          <button style={styles.addBtn} onClick={handleSubmit} disabled={!title.trim()}>Add Task</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function RemediationPunchList() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [filterPhase, setFilterPhase] = useState("all");
  const [groupBy, setGroupBy] = useState("phase"); // phase | category | priority | status
  const [generated, setGenerated] = useState(false);

  useEffect(() => {
    fetchAndGenerate();
  }, []);

  const fetchAndGenerate = async () => {
    try {
      setLoading(true);
      const customerId = localStorage.getItem("customer_id") || "cust_techcorpin_575148";
      const res = await fetch(`${API_BASE}/api/assessments/current?customer_id=${customerId}`);
      if (!res.ok) throw new Error("Failed to load assessment");
      const data = await res.json();
      const responses = data.assessment?.responses || data.responses || {};
      const generated = generateTasksFromGaps(responses);
      setTasks(generated);
      setGenerated(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = (id, updates) => {
    setTasks((prev) => prev.map((t) => t.id === id ? { ...t, ...updates } : t));
  };

  const handleDelete = (id) => {
    setTasks((prev) => prev.filter((t) => t.id !== id));
  };

  const handleAdd = (task) => {
    setTasks((prev) => [...prev, task]);
  };

  // ── Filtering ──
  const filtered = tasks.filter((t) => {
    if (filterStatus !== "all" && t.status !== filterStatus) return false;
    if (filterPriority !== "all" && t.priority !== filterPriority) return false;
    if (filterPhase !== "all" && t.phase !== parseInt(filterPhase)) return false;
    return true;
  });

  // ── Grouping ──
  const grouped = {};
  for (const task of filtered) {
    let key;
    if (groupBy === "phase")    key = `Phase ${task.phase}`;
    if (groupBy === "category") key = task.categoryLabel;
    if (groupBy === "priority") key = PRIORITY_CONFIG[task.priority]?.label || task.priority;
    if (groupBy === "status")   key = STATUS_CONFIG[task.status]?.label || task.status;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(task);
  }

  // Sort group keys sensibly
  const sortedGroupKeys = Object.keys(grouped).sort((a, b) => {
    if (groupBy === "phase") return a.localeCompare(b);
    if (groupBy === "priority") {
      const order = ["Critical", "High", "Medium", "Low"];
      return order.indexOf(a) - order.indexOf(b);
    }
    return a.localeCompare(b);
  });

  // ── Stats ──
  const total = tasks.length;
  const complete = tasks.filter((t) => t.status === "complete").length;
  const inProgress = tasks.filter((t) => t.status === "in_progress").length;
  const blocked = tasks.filter((t) => t.status === "blocked").length;
  const pct = total > 0 ? Math.round((complete / total) * 100) : 0;

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.headerTitle}>Remediation Punch List</h1>
          <p style={styles.headerSub}>Track and manage security remediation tasks across your organization</p>
        </div>
        <button style={styles.addTaskBtn} onClick={() => setShowAddModal(true)}>
          + Add Task
        </button>
      </div>

      {/* Progress Bar */}
      <div style={styles.progressSection}>
        <div style={styles.statsRow}>
          <div style={styles.stat}>
            <span style={styles.statNum}>{total}</span>
            <span style={styles.statLabel}>Total Tasks</span>
          </div>
          <div style={styles.stat}>
            <span style={{ ...styles.statNum, color: "#22c55e" }}>{complete}</span>
            <span style={styles.statLabel}>Complete</span>
          </div>
          <div style={styles.stat}>
            <span style={{ ...styles.statNum, color: "#f59e0b" }}>{inProgress}</span>
            <span style={styles.statLabel}>In Progress</span>
          </div>
          <div style={styles.stat}>
            <span style={{ ...styles.statNum, color: "#ef4444" }}>{blocked}</span>
            <span style={styles.statLabel}>Blocked</span>
          </div>
          <div style={{ ...styles.stat, flex: 2 }}>
            <div style={styles.progressBar}>
              <div style={{ ...styles.progressFill, width: `${pct}%` }} />
            </div>
            <span style={styles.statLabel}>{pct}% Complete</span>
          </div>
        </div>
      </div>

      {/* Filters + Group By */}
      <div style={styles.toolbar}>
        <div style={styles.filters}>
          <span style={styles.filterLabel}>Filter:</span>
          <select style={styles.filterSelect} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select style={styles.filterSelect} value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}>
            <option value="all">All Priority</option>
            {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select style={styles.filterSelect} value={filterPhase} onChange={(e) => setFilterPhase(e.target.value)}>
            <option value="all">All Phases</option>
            <option value="1">Phase 1</option>
            <option value="2">Phase 2</option>
            <option value="3">Phase 3</option>
          </select>
        </div>
        <div style={styles.groupBy}>
          <span style={styles.filterLabel}>Group by:</span>
          {["phase", "category", "priority", "status"].map((g) => (
            <button
              key={g}
              style={{ ...styles.groupBtn, ...(groupBy === g ? styles.groupBtnActive : {}) }}
              onClick={() => setGroupBy(g)}
            >
              {g.charAt(0).toUpperCase() + g.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Task List */}
      <div style={styles.content}>
        {loading ? (
          <div style={styles.emptyState}>Loading assessment data...</div>
        ) : error ? (
          <div style={{ ...styles.emptyState, color: "#ef4444" }}>⚠ {error}</div>
        ) : filtered.length === 0 ? (
          <div style={styles.emptyState}>No tasks match current filters.</div>
        ) : (
          sortedGroupKeys.map((groupKey) => (
            <div key={groupKey} style={styles.group}>
              <div style={styles.groupHeader}>
                <span style={styles.groupTitle}>{groupKey}</span>
                <span style={styles.groupCount}>{grouped[groupKey].length} tasks</span>
              </div>
              <div style={styles.taskList}>
                {grouped[groupKey].map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    onUpdate={handleUpdate}
                    onDelete={handleDelete}
                    allTasks={tasks}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Task Modal */}
      {showAddModal && (
        <AddTaskModal onAdd={handleAdd} onClose={() => setShowAddModal(false)} />
      )}
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  page: {
    minHeight: "100vh",
    background: "#080b12",
    color: "#e2e8f0",
    fontFamily: "'Syne', 'DM Sans', system-ui, sans-serif",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "32px 40px 24px",
    borderBottom: "1px solid #1a2236",
  },
  headerLeft: { display: "flex", flexDirection: "column", gap: 6 },
  headerTitle: {
    fontSize: 26, fontWeight: 800, color: "#f1f5f9",
    margin: 0, letterSpacing: "-0.03em",
  },
  headerSub: { fontSize: 13, color: "#64748b", margin: 0 },
  addTaskBtn: {
    padding: "10px 20px",
    background: "linear-gradient(135deg, #6d28d9, #4f46e5)",
    border: "none", borderRadius: 8,
    color: "#fff", fontSize: 14, fontWeight: 700,
    cursor: "pointer", letterSpacing: "0.01em",
    boxShadow: "0 4px 15px #6d28d930",
    alignSelf: "flex-start",
  },
  progressSection: {
    padding: "20px 40px",
    borderBottom: "1px solid #1a2236",
    background: "#0a0d16",
  },
  statsRow: {
    display: "flex", gap: 32, alignItems: "center",
  },
  stat: {
    display: "flex", flexDirection: "column", gap: 3,
  },
  statNum: {
    fontSize: 24, fontWeight: 800, color: "#f1f5f9",
    lineHeight: 1,
  },
  statLabel: { fontSize: 11, color: "#475569", letterSpacing: "0.06em", textTransform: "uppercase" },
  progressBar: {
    width: 200, height: 6,
    background: "#1a2236", borderRadius: 3, overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    background: "linear-gradient(90deg, #6d28d9, #22c55e)",
    borderRadius: 3, transition: "width 0.4s ease",
  },
  toolbar: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "14px 40px",
    borderBottom: "1px solid #1a2236",
    background: "#080b12",
    flexWrap: "wrap", gap: 12,
  },
  filters: { display: "flex", alignItems: "center", gap: 10 },
  filterLabel: { fontSize: 12, color: "#475569", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" },
  filterSelect: {
    background: "#0f1420", border: "1px solid #1a2236",
    borderRadius: 6, padding: "6px 10px",
    color: "#94a3b8", fontSize: 12, outline: "none",
  },
  groupBy: { display: "flex", alignItems: "center", gap: 6 },
  groupBtn: {
    padding: "5px 12px", background: "none",
    border: "1px solid #1a2236", borderRadius: 6,
    color: "#475569", fontSize: 12, cursor: "pointer",
  },
  groupBtnActive: {
    background: "#7c3aed18", borderColor: "#7c3aed60", color: "#a78bfa",
  },
  content: { padding: "24px 40px", display: "flex", flexDirection: "column", gap: 28 },
  group: { display: "flex", flexDirection: "column", gap: 8 },
  groupHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "8px 0", borderBottom: "1px solid #1a2236",
    marginBottom: 4,
  },
  groupTitle: { fontSize: 13, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.06em", textTransform: "uppercase" },
  groupCount: { fontSize: 11, color: "#475569", background: "#1a2236", borderRadius: 10, padding: "2px 8px" },
  taskList: { display: "flex", flexDirection: "column", gap: 6 },
  taskCard: {
    background: "#0f1420", border: "1px solid #1a2236",
    borderRadius: 10, overflow: "hidden",
    transition: "border-color 0.15s",
  },
  taskMain: {
    display: "flex", alignItems: "center", gap: 14, padding: "14px 16px",
  },
  checkbox: {
    width: 20, height: 20, borderRadius: 5,
    border: "2px solid #2d3748", background: "none",
    cursor: "pointer", flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 12, color: "#22c55e", fontWeight: 700,
  },
  checkboxDone: {
    background: "#22c55e18", borderColor: "#22c55e",
  },
  taskContent: { flex: 1, display: "flex", flexDirection: "column", gap: 6, minWidth: 0 },
  taskTitleRow: { display: "flex", alignItems: "center", gap: 8 },
  taskTitle: { fontSize: 14, fontWeight: 500, color: "#e2e8f0", lineHeight: 1.3 },
  autoTag: {
    fontSize: 9, fontWeight: 700, letterSpacing: "0.08em",
    color: "#475569", background: "#1a2236",
    borderRadius: 3, padding: "1px 5px", textTransform: "uppercase",
    flexShrink: 0,
  },
  taskMeta: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  categoryTag: {
    fontSize: 11, color: "#64748b",
    background: "#1a2236", borderRadius: 4, padding: "2px 7px",
  },
  phaseTag: {
    fontSize: 11, color: "#7c3aed",
    background: "#7c3aed15", borderRadius: 4, padding: "2px 7px",
  },
  assigneeTag: { fontSize: 11, color: "#64748b" },
  taskActions: { display: "flex", alignItems: "center", gap: 8, flexShrink: 0 },
  badge: {
    fontSize: 11, fontWeight: 600, borderRadius: 5,
    padding: "4px 9px", border: "1px solid transparent",
    letterSpacing: "0.03em",
  },
  dropdown: {
    position: "absolute", top: "100%", right: 0, zIndex: 100,
    background: "#111827", border: "1px solid #1a2236",
    borderRadius: 8, overflow: "hidden", minWidth: 130,
    boxShadow: "0 8px 24px #00000060",
    marginTop: 4,
  },
  dropdownItem: {
    display: "block", width: "100%", padding: "9px 14px",
    background: "none", border: "none",
    fontSize: 12, fontWeight: 600, cursor: "pointer", textAlign: "left",
  },
  expandBtn: {
    width: 28, height: 28, background: "#1a2236",
    border: "none", borderRadius: 6,
    color: "#64748b", fontSize: 10, cursor: "pointer",
  },
  deleteBtn: {
    width: 28, height: 28, background: "none",
    border: "1px solid #1a2236", borderRadius: 6,
    color: "#475569", fontSize: 11, cursor: "pointer",
  },
  taskDetail: {
    padding: "0 16px 16px 50px",
    borderTop: "1px solid #1a2236",
    background: "#0a0d16",
    display: "flex", flexDirection: "column", gap: 12,
    paddingTop: 14,
  },
  detailGrid: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 },
  detailField: { display: "flex", flexDirection: "column", gap: 5 },
  detailLabel: {
    fontSize: 10, fontWeight: 700, color: "#475569",
    letterSpacing: "0.08em", textTransform: "uppercase",
  },
  detailInput: {
    background: "#111827", border: "1px solid #1a2236",
    borderRadius: 6, padding: "7px 10px",
    color: "#e2e8f0", fontSize: 12, outline: "none",
  },
  detailSelect: {
    background: "#111827", border: "1px solid #1a2236",
    borderRadius: 6, padding: "7px 10px",
    color: "#e2e8f0", fontSize: 12, outline: "none",
  },
  detailTextarea: {
    background: "#111827", border: "1px solid #1a2236",
    borderRadius: 6, padding: "8px 10px",
    color: "#e2e8f0", fontSize: 12, outline: "none",
    resize: "vertical", fontFamily: "inherit", lineHeight: 1.5,
  },
  emptyState: { color: "#475569", fontSize: 14, textAlign: "center", padding: "60px 0" },
  // Modal
  modalOverlay: {
    position: "fixed", inset: 0, background: "#00000080",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    background: "#111827", border: "1px solid #1a2236",
    borderRadius: 14, width: 560, maxWidth: "90vw",
    boxShadow: "0 24px 60px #000000a0",
  },
  modalHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "20px 24px", borderBottom: "1px solid #1a2236",
  },
  modalTitle: { fontSize: 16, fontWeight: 700, color: "#f1f5f9", margin: 0 },
  modalClose: {
    background: "none", border: "none", color: "#64748b",
    fontSize: 16, cursor: "pointer",
  },
  modalBody: { padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 },
  modalFooter: {
    display: "flex", justifyContent: "flex-end", gap: 10,
    padding: "16px 24px", borderTop: "1px solid #1a2236",
  },
  cancelBtn: {
    padding: "9px 18px", background: "none",
    border: "1px solid #1a2236", borderRadius: 8,
    color: "#64748b", fontSize: 13, cursor: "pointer",
  },
  addBtn: {
    padding: "9px 20px",
    background: "linear-gradient(135deg, #6d28d9, #4f46e5)",
    border: "none", borderRadius: 8,
    color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
  },
};

